import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useT, type Replacements } from '../../i18n';
import { Icon } from './Icon';
import { cx } from './classNames';
import styles from './Toast.module.css';

export type ToastTone = 'success' | 'error' | 'info';

export interface ToastAction {
  labelKey?: string;
  label?: string;
  onClick: () => void;
}

export interface ToastOptions {
  titleKey?: string;
  title?: string;
  titleReplacements?: Replacements;
  body?: string;
  bodyKey?: string;
  bodyReplacements?: Replacements;
  tone?: ToastTone;
  duration?: number;
  action?: ToastAction;
}

interface ToastEntry extends Required<Pick<ToastOptions, 'tone' | 'duration'>> {
  id: string;
  titleKey?: string;
  title?: string;
  titleReplacements?: Replacements;
  body?: string;
  bodyKey?: string;
  bodyReplacements?: Replacements;
  action?: ToastAction;
}

export interface ToastContextValue {
  toast: (options: ToastOptions) => string;
  dismiss: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const TONE_ICONS: Record<ToastTone, string> = {
  success: 'check',
  error: 'warning',
  info: 'info',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const { t } = useT();
  const [toasts, setToasts] = useState<ToastEntry[]>([]);
  const timersRef = useRef<Map<string, number>>(new Map());
  const nextIdRef = useRef(0);

  const dismiss = useCallback((id: string) => {
    const timer = timersRef.current.get(id);
    if (timer !== undefined) {
      window.clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((current) => current.filter((item) => item.id !== id));
  }, []);

  const toast = useCallback(
    (options: ToastOptions) => {
      const id = `toast-${nextIdRef.current}`;
      nextIdRef.current += 1;
      const entry: ToastEntry = {
        id,
        tone: options.tone ?? 'info',
        duration: options.duration ?? (options.action ? 8000 : 4000),
        title: options.title,
        titleKey: options.titleKey,
        titleReplacements: options.titleReplacements,
        body: options.body,
        bodyKey: options.bodyKey,
        bodyReplacements: options.bodyReplacements,
        action: options.action,
      };

      setToasts((current) => [...current, entry]);
      const timer = window.setTimeout(() => dismiss(id), entry.duration);
      timersRef.current.set(id, timer);
      return id;
    },
    [dismiss],
  );

  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((timer) => window.clearTimeout(timer));
      timers.clear();
    };
  }, []);

  const value = useMemo<ToastContextValue>(() => ({ toast, dismiss }), [dismiss, toast]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className={styles.viewport} aria-live="polite" aria-relevant="additions removals">
        {toasts.map((item) => {
          const title = item.titleKey ? t(item.titleKey, item.titleReplacements) : item.title;
          const body = item.bodyKey ? t(item.bodyKey, item.bodyReplacements) : item.body;
          return (
            <div key={item.id} className={cx(styles.toast, styles[item.tone])} role={item.tone === 'error' ? 'alert' : 'status'}>
              <Icon name={TONE_ICONS[item.tone]} size={24} className={styles.icon} />
              <div className={styles.body}>
                {title ? <strong className={styles.title}>{title}</strong> : null}
                {body ? <span className={styles.message}>{body}</span> : null}
                {item.action ? (
                  <button
                    type="button"
                    className={styles.actionButton}
                    aria-label={item.action.labelKey ? t(item.action.labelKey) : item.action.label}
                    onClick={() => {
                      item.action?.onClick();
                      dismiss(item.id);
                    }}
                  >
                    {item.action.labelKey ? t(item.action.labelKey) : item.action.label}
                  </button>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const value = useContext(ToastContext);
  if (!value) throw new Error('useToast must be used within ToastProvider');
  return value;
}
